import type { Payment } from '~/api/graphql/generated/types'

// Utility functions for precise decimal arithmetic
export const roundToTwoDecimals = (num: number): number =>
  Math.round((num + Number.EPSILON) * 100) / 100

export const subtractDecimals = (a: number, b: number): number =>
  roundToTwoDecimals(a - b)

export const addDecimals = (a: number, b: number): number =>
  roundToTwoDecimals(a + b)

export interface PaymentSelectionState {
  partiallyAppliedPayments: Record<string, number>
  remainingBalance: number
  selectedPayments: Payment[]
  totalCreditAmount: number
}

export interface AutoSelectResult {
  currentTotalCredit: number
  partiallyApplied: Record<string, number>
  selected: Payment[]
  workingRemainingBalance: number
}

export interface RecalculateResult {
  balance: number
  newPartiallyApplied: Record<string, number>
  total: number
}

export interface PaymentDeselectionResult {
  balance: number
  newPartiallyApplied: Record<string, number>
  newSelectedPayments: Payment[]
  total: number
}

export interface PaymentSelectionResult {
  newPartiallyAppliedPayments: Record<string, number>
  newRemainingBalance: number
  newSelectedPayments: Payment[]
  newTotalCreditAmount: number
}

export const autoSelectPayments = (
  unappliedPayments: Payment[],
  remainingBalance: number,
): AutoSelectResult => {
  const sortedPayments = [...unappliedPayments].sort(
    (a, b) =>
      new Date(a.creationDate).getTime() - new Date(b.creationDate).getTime(),
  ) // Sort by creation date ascending (oldest first)

  const selected: Payment[] = []
  const partiallyApplied: { [paymentId: string]: number } = {}
  let workingRemainingBalance = remainingBalance
  let currentTotalCredit = 0

  sortedPayments.forEach((payment) => {
    if (selected.length >= 3 || workingRemainingBalance <= 0) {
      return
    }

    const paymentAmount = payment.unappliedAmount || 0

    if (paymentAmount <= workingRemainingBalance) {
      selected.push(payment)
      currentTotalCredit = addDecimals(currentTotalCredit, paymentAmount)
      workingRemainingBalance = subtractDecimals(
        workingRemainingBalance,
        paymentAmount,
      )
    } else if (workingRemainingBalance > 0) {
      selected.push(payment)
      const appliedAmount = workingRemainingBalance
      const unappliedAmount = subtractDecimals(
        paymentAmount,
        workingRemainingBalance,
      )

      currentTotalCredit = addDecimals(currentTotalCredit, appliedAmount)
      workingRemainingBalance = 0

      partiallyApplied[payment.id] = unappliedAmount
    }
  })

  return {
    selected,
    partiallyApplied,
    workingRemainingBalance,
    currentTotalCredit,
  }
}

export const recalculatePartiallyAppliedPayments = (
  payments: Payment[],
  partiallyApplied: Record<string, number>,
  workingBalance: number,
  workingTotal: number,
): RecalculateResult => {
  const newPartiallyApplied = { ...partiallyApplied }
  let balance = workingBalance
  let total = workingTotal

  payments.forEach((selectedPayment) => {
    const currentPartialAmount = newPartiallyApplied[selectedPayment.id] || 0

    if (balance > 0 && currentPartialAmount > 0) {
      const additionalAmount = Math.min(currentPartialAmount, balance)
      balance = subtractDecimals(balance, additionalAmount)
      total = addDecimals(total, additionalAmount)

      const newPartialAmount = subtractDecimals(
        currentPartialAmount,
        additionalAmount,
      )
      if (newPartialAmount > 0) {
        newPartiallyApplied[selectedPayment.id] = newPartialAmount
      } else {
        delete newPartiallyApplied[selectedPayment.id]
      }
    }
  })

  return { newPartiallyApplied, balance, total }
}

export const handlePaymentDeselection = (
  payment: Payment,
  currentPaymentAmount: number,
  currentState: PaymentSelectionState,
): PaymentDeselectionResult => {
  const newSelectedPayments = currentState.selectedPayments.filter(
    (p) => p.id !== payment.id,
  )
  const partialAmount = currentState.partiallyAppliedPayments[payment.id] || 0
  const appliedAmount = subtractDecimals(currentPaymentAmount, partialAmount)

  const workingBalance = addDecimals(
    currentState.remainingBalance,
    appliedAmount,
  )
  const workingTotal = subtractDecimals(
    currentState.totalCreditAmount,
    appliedAmount,
  )

  const newPartiallyApplied = { ...currentState.partiallyAppliedPayments }
  delete newPartiallyApplied[payment.id]

  const {
    newPartiallyApplied: finalPartiallyApplied,
    balance,
    total,
  } = recalculatePartiallyAppliedPayments(
    newSelectedPayments,
    newPartiallyApplied,
    workingBalance,
    workingTotal,
  )

  return {
    newSelectedPayments,
    newPartiallyApplied: finalPartiallyApplied,
    balance,
    total,
  }
}

export const handlePaymentSelection = (
  payment: Payment,
  currentPaymentAmount: number,
  currentState: PaymentSelectionState,
): PaymentSelectionResult => {
  if (currentState.remainingBalance <= 0) {
    return {
      newSelectedPayments: currentState.selectedPayments,
      newTotalCreditAmount: currentState.totalCreditAmount,
      newRemainingBalance: currentState.remainingBalance,
      newPartiallyAppliedPayments: currentState.partiallyAppliedPayments,
    }
  }

  const newSelectedPayments = [...currentState.selectedPayments, payment]

  if (currentPaymentAmount <= currentState.remainingBalance) {
    return {
      newSelectedPayments,
      newTotalCreditAmount: addDecimals(
        currentState.totalCreditAmount,
        currentPaymentAmount,
      ),
      newRemainingBalance: subtractDecimals(
        currentState.remainingBalance,
        currentPaymentAmount,
      ),
      newPartiallyAppliedPayments: currentState.partiallyAppliedPayments,
    }
  }
  const appliedAmount = currentState.remainingBalance
  const unappliedAmount = subtractDecimals(
    currentPaymentAmount,
    currentState.remainingBalance,
  )

  return {
    newSelectedPayments,
    newTotalCreditAmount: addDecimals(
      currentState.totalCreditAmount,
      appliedAmount,
    ),
    newRemainingBalance: 0,
    newPartiallyAppliedPayments: {
      ...currentState.partiallyAppliedPayments,
      [payment.id]: unappliedAmount,
    },
  }
}

export const handleSelectPayment = (
  payment: Payment,
  currentState: PaymentSelectionState,
): PaymentSelectionResult | PaymentDeselectionResult => {
  const currentPaymentAmount = payment.unappliedAmount ?? 0
  const isCurrentlySelected = currentState.selectedPayments.some(
    (p) => p.id === payment.id,
  )

  if (isCurrentlySelected) {
    return handlePaymentDeselection(payment, currentPaymentAmount, currentState)
  }
  return handlePaymentSelection(payment, currentPaymentAmount, currentState)
}

export const isPaymentSelected = (
  payment: Payment,
  selectedPayments: Payment[],
): boolean => selectedPayments.some((p) => p.id === payment.id)

export const isPaymentDisabled = (
  payment: Payment,
  selectedPayments: Payment[],
  remainingBalance: number,
): boolean =>
  !isPaymentSelected(payment, selectedPayments) && remainingBalance <= 0
